import { useState, useEffect, memo } from 'react';
import { ReactComponent as Community } from '@Assets/community.svg';
import { Avatar, Popover, Skeleton, Tooltip } from '@mui/material';
import ProfileBanner from '@CommonRepresentationalDisplay/ProfileBanner';
import UserInfoService from '@Helpers/URLHelpers/UserInfoService';
import ErrorHandler from '@Root/ErrorHandler';
import './FullUserAvatar.scss';

function FullUserAvatar(props) {
    const {
        profileId,
        size = 'medium',
        popover = false,
        showUserName = false,
        showFullName = true,
        onClick,
        style,
        className: customClassName = '',
    } = props;

    const [user, setUser] = useState({});
    const [loading, setLoading] = useState(false);
    const [anchorEl, setAnchorEl] = useState(null);

    const userInfoService = new UserInfoService();
    const errorHandler = new ErrorHandler();

    const className = 'fullUserAvatar';

    const colorList = [
        '#2563eb',
        '#7c3aed',
        '#0891b2',
        '#059669',
        '#d97706',
        '#dc2626',
        '#db2777',
        '#4f46e5',
        '#0f766e',
        '#9333ea',
    ];

    useEffect(() => {
        let mounted = true;

        const loadUser = async () => {
            if (profileId === -2) {
                if (mounted) {
                    setUser({
                        id: -2,
                        fullName: 'Community',
                        userName: 'community',
                    });
                }
                return;
            }

            if (profileId === 0 || !profileId) {
                if (mounted) {
                    setUser({
                        id: 0,
                        fullName: 'Unassigned',
                        userName: 'unassigned',
                    });
                }
                return;
            }

            setLoading(true);

            try {
                const response = await userInfoService.getUserById(profileId);

                if (response?.status >= 400) {
                    throw new Error('Unable to load user');
                }

                const userObj = JSON.parse(response.body);

                if (mounted) {
                    setUser(userObj || {});
                }
            } catch (err) {
                errorHandler.logError('FullUserAvatar', err);

                if (mounted) {
                    setUser({
                        id: profileId,
                        fullName: 'Unknown User',
                        userName: '',
                    });
                }
            } finally {
                if (mounted) {
                    setLoading(false);
                }
            }
        };

        loadUser();

        return () => {
            mounted = false;
        };
    }, [profileId]);

    const getInitials = (value) => {
        if (!value) return 'U';

        const cleanValue = value.trim();

        if (!cleanValue) return 'U';

        const parts = cleanValue.split(/\s+/);

        if (parts.length === 1) {
            return parts[0].substring(0, 2).toUpperCase();
        }

        return (
            parts[0].charAt(0) +
            parts[parts.length - 1].charAt(0)
        ).toUpperCase();
    };

    const getAvatarColor = (value) => {
        if (!value) return colorList[0];

        const index =
            value
                .split('')
                .reduce((sum, char) => sum + char.charCodeAt(0), 0) %
            colorList.length;

        return colorList[index];
    };

    const getSize = () => {
        switch (size) {
            case 'large':
                return 42;
            case 'medium':
                return 32;
            case 'small':
            default:
                return 24;
        }
    };

    const handleMouseEnter = (event) => {
        if (popover && profileId > 0) {
            setAnchorEl(event.currentTarget);
        }
    };

    const handleMouseLeave = () => {
        if (popover) {
            setAnchorEl(null);
        }
    };

    const handlePopoverEnter = () => {
        // Keeps the popover open when moving toward it.
        if (popover && profileId > 0) {
            // intentionally empty; popover is controlled by avatar hover
        }
    };

    const getDisplayName = () => {
        if (user?.fullName) {
            return user.fullName;
        }

        return 'Unknown User';
    };

    const getShortName = () => {
        const name = getDisplayName();

        if (!name) {
            return '';
        }

        const parts = name.trim().split(/\s+/);

        if (parts.length === 1) {
            return parts[0];
        }

        return `${parts[0]} ${parts[parts.length - 1].charAt(0)}.`;
    };

    if (loading) {
        return (
            <div
                className={`${className} ${className}--${size} ${customClassName}`}
                style={style}
            >
                <Skeleton
                    variant="circular"
                    width={getSize()}
                    height={getSize()}
                />

                {showFullName && (
                    <div className={`${className}__loadingText`}>
                        <Skeleton width={90} height={16} />
                    </div>
                )}
            </div>
        );
    }

    /*
     * Community
     */
    if (profileId === -2) {
        return (
            <div
                className={`${className} ${className}--${size} ${className}--community ${customClassName}`}
                style={style}
                onClick={onClick}
            >
                <div className={`${className}__avatar`}>
                    <Community
                        className={`${className}__communityIcon`}
                    />
                </div>

                {showFullName && (
                    <span className={`${className}__name`}>
                        Community
                    </span>
                )}
            </div>
        );
    }

    const isUnassigned = profileId === 0 || !profileId;

    const avatarColor = getAvatarColor(user?.fullName);

    return (
        <>
            <Tooltip
                title={
                    popover
                        ? ''
                        : `${getDisplayName()}${
                              user?.userName
                                  ? ` • @${user.userName}`
                                  : ''
                          }`
                }
                arrow
                disableHoverListener={popover}
            >
                <div
                    className={[
                        className,
                        `${className}--${size}`,
                        isUnassigned
                            ? `${className}--unassigned`
                            : '',
                        onClick
                            ? `${className}--clickable`
                            : '',
                        customClassName,
                    ]
                        .filter(Boolean)
                        .join(' ')}
                    style={style}
                    onMouseEnter={handleMouseEnter}
                    onMouseLeave={handleMouseLeave}
                    onClick={onClick}
                >
                    <div className={`${className}__avatarWrapper`}>
                        <Avatar
                            src={
                                user?.photo &&
                                user.photo !== 'null'
                                    ? user.photo
                                    : undefined
                            }
                            alt={getDisplayName()}
                            className={`${className}__avatar`}
                            sx={{
                                width: getSize(),
                                height: getSize(),
                                bgcolor: avatarColor,
                            }}
                        >
                            {getInitials(user?.fullName)}
                        </Avatar>

                        {!isUnassigned && (
                            <span
                                className={`${className}__statusDot`}
                            />
                        )}
                    </div>

                    {(showUserName || showFullName) && (
                        <div className={`${className}__content`}>
                            {showFullName && (
                                <span className={`${className}__name`}>
                                    {getShortName()}
                                </span>
                            )}

                            {showUserName &&
                                user?.userName && (
                                    <span
                                        className={`${className}__username`}
                                    >
                                        @{user.userName}
                                    </span>
                                )}
                        </div>
                    )}
                </div>
            </Tooltip>

            {popover && profileId > 0 && (
                <Popover
                    open={Boolean(anchorEl)}
                    anchorEl={anchorEl}
                    onClose={() => setAnchorEl(null)}
                    disableRestoreFocus
                    sx={{
                        pointerEvents: 'none',
                    }}
                    slotProps={{
                        paper: {
                            className: `${className}__popover`,
                        },
                    }}
                    anchorOrigin={{
                        vertical: 'top',
                        horizontal: 'center',
                    }}
                    transformOrigin={{
                        vertical: 'bottom',
                        horizontal: 'center',
                    }}
                >
                    <div
                        onMouseEnter={handlePopoverEnter}
                        className={`${className}__popoverContent`}
                    >
                        <ProfileBanner profileId={profileId} />
                    </div>
                </Popover>
            )}
        </>
    );
}

export default memo(FullUserAvatar, arePropsEqual);

function arePropsEqual(prevProps, nextProps) {
    return (
        prevProps?.profileId === nextProps?.profileId &&
        prevProps?.size === nextProps?.size &&
        prevProps?.showUserName === nextProps?.showUserName &&
        prevProps?.showFullName === nextProps?.showFullName &&
        prevProps?.popover === nextProps?.popover
    );
}
